var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc funner chart
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Polygon, Line } from '../../element';
import { SeriesModel } from '../../model';
import { eventGraph } from '../../action';
import { LegendType } from '../../interface/legend';
import { getPercentOrNumberValue, getPercent } from '../../utils/math';
import normalizeToArray from '../../utils/normalizeToArray';
import merge from '../../utils/merge';
import LinearScale from '../../scale/linear';
import { alignBox } from '../../utils/boxLayout';
import { generateColors } from '../../utils/color';
import { pixelOptimize } from '../../utils/line1px';
import { getRectLabelPosition } from '../../utils/textLayout';
import { createRichText } from '../../rich';
import { registerChart } from '../index';
import measureText from '../../utils/measureText';
import { SelectController } from '../../utils/select-controller';
function getDefaultLabelOption() {
    return {
        show: true,
        distance: 54,
        offsetX: 0,
        offsetY: 0,
        rotate: null,
        position: 'both',
        placement: 'on',
        alignTo: 'none',
        margin: 24,
        color: null,
        fontSize: 12,
        formatter: function (point) { return point.name + point.value; },
        formatter2: function (point) { return point.percent; },
    };
}
/**
 * funnel model
 */
var FunnelSeriesModel = /** @class */ (function (_super) {
    __extends(FunnelSeriesModel, _super);
    function FunnelSeriesModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'funnel';
        _this.legendType = LegendType.DATUM_LEGEND;
        _this._gapAreaSelectController = new SelectController();
        _this.toggleHighlightGapArea = function (element, active) {
            element.setAttr(__assign(__assign({}, _this.getShapeStyle(_this.option.gapAreaStyle)), (active ? _this.getShapeStyle(_this.option.hover.gapAreaStyle) : null)));
        };
        _this.handleGapAreaClick = function (event) {
            if (_this._hasInteraction('single-select')) {
                _this._gapAreaSelectController.unselectAll();
                _this._gapAreaSelectController.select(event.point.name, 'single');
                var node_1 = event.mouse.target;
                var items = node_1.parentNode.childNodes;
                var normalStyle_1 = _this.getShapeStyle(_this.option.gapAreaStyle);
                var selectedStyle = _this.getShapeStyle(_this.option.selected.gapAreaStyle);
                items.forEach(function (item) { return item !== node_1 && item.setAttr(normalStyle_1); });
                node_1.setAttr(__assign(__assign({}, normalStyle_1), selectedStyle));
            }
        };
        _this.handleGapAreaMouseEnter = function (event) {
            if (_this._hasInteraction('hover')) {
                _this.toggleHighlightGapArea(event.mouse.target, true);
            }
        };
        _this.hanldeGapAreaMouseMove = function (event) {
            var _a;
            if (_this._hasInteraction('tooltip')) {
                (_a = _this.chart.getComponentModel('tooltip')) === null || _a === void 0 ? void 0 : _a.showTooltip({
                    title: '',
                    mouse: event.mouse,
                    points: [event.point],
                });
            }
        };
        _this.handleGapAreaMouseLeave = function (event) {
            var _a;
            if (_this._hasInteraction('hover')) {
                _this.toggleHighlightGapArea(event.mouse.target, false);
            }
            if (_this._hasInteraction('tooltip')) {
                (_a = _this.chart.getComponentModel('tooltip')) === null || _a === void 0 ? void 0 : _a.hideTooltip();
            }
        };
        return _this;
    }
    FunnelSeriesModel.prevProcessOption = function (option) {
        option.series.forEach(function (series) {
            if (series.type === 'funnel' && series.label) {
                series.label = normalizeToArray(series.label);
            }
            else if (lodash.isUndefined(series.label)) {
                series.label = [getDefaultLabelOption()];
            }
            else if (!series.label) {
                series.label = [];
            }
        });
    };
    FunnelSeriesModel.prototype.getDefaultOption = function (option) {
        return __assign(__assign({}, _super.prototype.getDefaultOption.call(this)), { type: 'funnel', name: '', width: '58%', height: '68%', minSize: 0, align: 'center', verticalAlign: 'middle', direction: 'TB', sort: 'desc', gap: 0, showGapArea: false, gapAreaInteractions: ['tooltip', 'hover'], shape: 'trapezoid', endWithRect: false, itemAlign: 'center', connectLine: {
                show: true,
                lineWidth: 1,
                stroke: '#878EA8',
            }, encode: {
                name: '',
                value: '',
                color: [
                    '$index',
                    function (value, dataIndex, seriesModel) {
                        var seriesColor = seriesModel.getSeriesColor();
                        if (lodash.isString(seriesColor)) {
                            var colors = generateColors(seriesColor).reverse();
                            var count = seriesModel.getDataset().count();
                            if (dataIndex <= 10) {
                                var midIndex = Math.min(Math.round(count / 2) - 1, 5);
                                var colorIndex = 5 - midIndex + dataIndex;
                                return colors[colorIndex];
                            }
                            return colors[10];
                        }
                        return seriesColor;
                    },
                ],
            }, label: option.label
                .filter(function (val) { return val; })
                .map(function (labelOption) { return merge(getDefaultLabelOption(), labelOption); }), shapeStyle: {
                stroke: null,
                lineWidth: 0,
                cursor: 'pointer',
                shadowBlur: 0,
                shadowOffsetX: 0,
                shadowOffsetY: 0,
                shadowColor: 'rgba(0, 51, 120, .2)',
            }, hover: {
                shapeStyle: {
                    stroke: null,
                    fill: null,
                    lineWidth: null,
                    shadowBlur: 8,
                },
                gapAreaStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            }, gapAreaStyle: {
                fill: '#EFF1F9',
                stroke: null,
                lineWidth: 0,
            }, selected: {
                shapeStyle: {
                    stroke: null,
                    fill: null,
                    lineWidth: null,
                },
                gapAreaStyle: {},
            }, visible: true, showLegend: true, animate: null, showBackground: false, backgroundStyle: {
                fill: '#EFF1F9',
            } });
    };
    FunnelSeriesModel.prototype.getInitialState = function () {
        return {
            width: 0,
            height: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            points: [],
            needAnimation: this.needAnimation(),
        };
    };
    FunnelSeriesModel.prototype.init = function () {
        this._sortData();
        this.registerEvent('gapArea:click', this.handleGapAreaClick);
        this.registerEvent('gapArea:mousemove', this.hanldeGapAreaMouseMove);
        this.registerEvent('gapArea:mouseenter', this.handleGapAreaMouseEnter);
        this.registerEvent('gapArea:mouseleave', this.handleGapAreaMouseLeave);
    };
    FunnelSeriesModel.prototype.beforeRender = function () {
        var _this = this;
        var _a = this.option, align = _a.align, verticalAlign = _a.verticalAlign, gap = _a.gap, direction = _a.direction, shape = _a.shape, itemAlign = _a.itemAlign, endWithRect = _a.endWithRect, sort = _a.sort;
        var _b = this.getFacetBBox(), top = _b.top, left = _b.left, width = _b.width, height = _b.height;
        var isHorizontally = this._isHorizontally();
        var funnelWidth = getPercentOrNumberValue(this.option.width, width);
        var funnelHeight = getPercentOrNumberValue(this.option.height, height);
        var _c = alignBox({
            x: left,
            y: top,
            width: width,
            height: height,
        }, funnelWidth, funnelHeight, align, verticalAlign), funnelLeft = _c.x, funnelTop = _c.y;
        var funnelRight = funnelLeft + funnelWidth;
        var funnelBottom = funnelTop + funnelHeight;
        var cx = (funnelRight + funnelLeft) / 2;
        var cy = (funnelTop + funnelBottom) / 2;
        var valueField = this.option.encode.value;
        var maxVisibleValue = this._dataset.max(valueField, function (record, dataIndex) { return !_this.invisibleItems.has(dataIndex); });
        var points = [];
        var minWidth = getPercentOrNumberValue(this.option.minSize, isHorizontally ? funnelHeight : funnelWidth);
        var widthScale = new LinearScale(0, 1, minWidth, isHorizontally ? funnelHeight : funnelWidth);
        var data = this._dataset.getData();
        var visibleCount = this._dataset.count(function (record, dataIndex) { return !_this.invisibleItems.has(dataIndex); });
        var itemHeight = visibleCount > 0
            ? ((isHorizontally ? funnelWidth : funnelHeight) - gap * (visibleCount - 1)) / visibleCount
            : 0;
        var visibleIndexList = [];
        data.forEach(function (record, dataIndex) {
            if (!_this.invisibleItems.has(dataIndex)) {
                visibleIndexList.push(dataIndex);
            }
        });
        var closestVisibleIndex = -1;
        var prevEndWidth = isHorizontally ? funnelHeight : funnelWidth;
        data.map(function (record, dataIndex) {
            var encode = _this._encode.getEncodeData(record, dataIndex);
            var name = encode.name, ovalue = encode.value;
            var value = parseFloat(ovalue);
            var visibleIndex = visibleIndexList.indexOf(dataIndex);
            var visible = visibleIndex !== -1;
            var nextVisibleDataIndex = visibleIndexList[visibleIndex + 1];
            var percent = getPercent(value, maxVisibleValue) / 100;
            var nextPercent = nextVisibleDataIndex >= 0
                ? getPercent(parseFloat(data[nextVisibleDataIndex][valueField]), maxVisibleValue) / 100
                : sort === 'none'
                    ? value / maxVisibleValue
                    : 0;
            if (visible) {
                closestVisibleIndex = visibleIndex;
            }
            var startWidth;
            var endWidth;
            if (visible) {
                startWidth = widthScale.scale(percent);
                prevEndWidth = endWidth = shape !== 'rect' ? widthScale.scale(nextPercent) : startWidth;
                if (endWithRect && visibleIndex === visibleIndexList.length - 1) {
                    prevEndWidth = endWidth = startWidth;
                }
            }
            else {
                startWidth = endWidth = prevEndWidth;
            }
            var centerWidth = (startWidth + endWidth) / 2;
            var startPosition;
            var endPosition;
            if (direction === 'TB') {
                startPosition = Math.max(funnelTop, funnelTop + closestVisibleIndex * (itemHeight + gap) + (visible ? 0 : itemHeight));
                endPosition = startPosition + (visible ? itemHeight : 0);
            }
            else if (direction === 'BT') {
                startPosition = Math.min(funnelBottom, funnelBottom - closestVisibleIndex * (itemHeight + gap) - (visible ? 0 : itemHeight));
                endPosition = startPosition - (visible ? itemHeight : 0);
            }
            else if (direction === 'LR') {
                startPosition = Math.max(funnelLeft, funnelLeft + closestVisibleIndex * (itemHeight + gap) + (visible ? 0 : itemHeight));
                endPosition = startPosition + (visible ? itemHeight : 0);
            }
            else if (direction === 'RL') {
                startPosition = Math.min(funnelRight, funnelRight - closestVisibleIndex * (itemHeight + gap) - (visible ? 0 : itemHeight));
                endPosition = startPosition - (visible ? itemHeight : 0);
            }
            var bbox = { top: 0, left: 0, right: 0, bottom: 0 };
            if (isHorizontally) {
                bbox.left = Math.min(startPosition, endPosition);
                bbox.right = Math.max(startPosition, endPosition);
                if (itemAlign === 'start') {
                    bbox.top = funnelTop;
                    bbox.bottom = funnelTop + centerWidth;
                }
                else if (itemAlign === 'center') {
                    bbox.top = cy - centerWidth / 2;
                    bbox.bottom = cy + centerWidth / 2;
                }
                else if (itemAlign === 'end') {
                    bbox.top = funnelBottom - centerWidth;
                    bbox.bottom = funnelBottom;
                }
            }
            else {
                bbox.top = Math.min(startPosition, endPosition);
                bbox.bottom = Math.max(startPosition, endPosition);
                // @ts-ignore  compatible itemAlign old left
                if (itemAlign === 'left' || itemAlign === 'start') {
                    bbox.left = funnelLeft;
                    bbox.right = funnelLeft + centerWidth;
                }
                else if (itemAlign === 'center') {
                    bbox.left = cx - centerWidth / 2;
                    bbox.right = cx + centerWidth / 2;
                    // @ts-ignore compatible itemAlign old right
                }
                else if (itemAlign === 'right' || itemAlign === 'end') {
                    bbox.right = funnelRight;
                    bbox.left = funnelRight - centerWidth;
                }
            }
            points.push({
                encode: encode,
                name: name,
                value: value,
                percent: (percent * 100).toFixed(2).replace('.00', '') + "%",
                startWidth: startWidth,
                endWidth: endWidth,
                startPosition: pixelOptimize(startPosition),
                endPosition: pixelOptimize(endPosition),
                bbox: bbox,
                visible: visible,
                visibleIndex: visibleIndex,
                nextVisibleDataIndex: nextVisibleDataIndex,
                selected: _this.selectedItems.has(record),
                color: encode.color,
                conversionRate: getPercent(nextPercent, percent) / 100,
                shapeRef: createRef(),
            });
        });
        this.setState({
            top: pixelOptimize(funnelTop),
            left: pixelOptimize(funnelLeft),
            right: pixelOptimize(funnelRight),
            bottom: pixelOptimize(funnelBottom),
            width: funnelWidth,
            height: funnelHeight,
            points: points,
        });
    };
    FunnelSeriesModel.prototype.afterRender = function () {
        if (this.state.needAnimation && this.visible() && this.state.points.length > 0) {
            this.state.needAnimation = false;
            this.chart.refresh();
        }
    };
    FunnelSeriesModel.prototype.updated = function () {
        this._sortData();
        this._gapAreaSelectController.unselectAll();
    };
    FunnelSeriesModel.prototype.getShapeAttr = function (point) {
        var hover = this.state.points[this.state.hoverPointIndex] === point;
        var color = point.color, selected = point.selected, visible = point.visible;
        var fill = hover ? this.getHoverColor(color) : color;
        return __assign(__assign(__assign({ opacity: visible ? 1 : 0, fill: fill }, this.getShapeStyle(this.option.shapeStyle)), (hover ? this.getShapeStyle(this.option.hover.shapeStyle) : null)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null));
    };
    FunnelSeriesModel.prototype.toggleHighlightPoint = function (dataIndex) {
        var _a;
        var point = this.state.points[dataIndex];
        var attr = this.getShapeAttr(point);
        (_a = point.shapeRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(attr);
    };
    FunnelSeriesModel.prototype.getGapAreaSelectController = function () {
        return this._gapAreaSelectController;
    };
    FunnelSeriesModel.prototype.toggleSelectGapArea = function (source, target, active) {
        var p1 = this.state.points[source];
        var p2 = this.state.points[target];
        var key = p1.name + " - " + p2.name;
        this._gapAreaSelectController.toggleSelectItem(key, active);
        this.chart.refresh();
    };
    FunnelSeriesModel.prototype.toggleSelectPoint = function (dataIndex, selected) {
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var point = this.state.points[dataIndex];
        var attr = this.getShapeAttr(point);
        point.shapeRef.current.setAttr(attr);
    };
    FunnelSeriesModel.prototype.getFunnelItemPoints = function (dataIndex) {
        var point = this.state.points[dataIndex];
        var itemAlign = this.option.itemAlign;
        var _a = this.state, left = _a.left, right = _a.right, top = _a.top, bottom = _a.bottom;
        var startPosition = point.startPosition, endPosition = point.endPosition;
        var startWidth = point.startWidth, endWidth = point.endWidth;
        if (this.state.needAnimation) {
            startWidth = endWidth = 0;
        }
        var p1;
        var p2;
        var p3;
        var p4;
        if (this._isHorizontally()) {
            var center = (top + bottom) / 2;
            if (itemAlign === 'start') {
                p1 = {
                    x: startPosition,
                    y: top + startWidth,
                };
                p2 = {
                    x: startPosition,
                    y: top,
                };
                p3 = {
                    x: endPosition,
                    y: top,
                };
                p4 = {
                    x: endPosition,
                    y: top + endWidth,
                };
            }
            else if (itemAlign === 'center') {
                p1 = {
                    x: startPosition,
                    y: center + startWidth / 2,
                };
                p2 = {
                    x: startPosition,
                    y: center - startWidth / 2,
                };
                p3 = {
                    x: endPosition,
                    y: center - endWidth / 2,
                };
                p4 = {
                    x: endPosition,
                    y: center + endWidth / 2,
                };
            }
            else if (itemAlign === 'end') {
                p1 = {
                    x: startPosition,
                    y: bottom,
                };
                p2 = {
                    x: startPosition,
                    y: bottom - startWidth,
                };
                p3 = {
                    x: endPosition,
                    y: bottom - endWidth,
                };
                p4 = {
                    x: endPosition,
                    y: bottom,
                };
            }
        }
        else {
            var center = (left + right) / 2;
            // @ts-ignore compatible itemAlign old eft
            if (itemAlign === 'left' || itemAlign === 'start') {
                p1 = {
                    x: left,
                    y: startPosition,
                };
                p2 = {
                    x: left + startWidth,
                    y: startPosition,
                };
                p3 = {
                    x: left + endWidth,
                    y: endPosition,
                };
                p4 = {
                    x: left,
                    y: endPosition,
                };
            }
            else if (itemAlign === 'center') {
                p1 = {
                    x: center - startWidth / 2,
                    y: startPosition,
                };
                p2 = {
                    x: center + startWidth / 2,
                    y: startPosition,
                };
                p3 = {
                    x: center + endWidth / 2,
                    y: endPosition,
                };
                p4 = {
                    x: center - endWidth / 2,
                    y: endPosition,
                };
                // @ts-ignore compatible itemAlign right
            }
            else if (itemAlign === 'right' || itemAlign === 'end') {
                p1 = {
                    x: right - startWidth,
                    y: startPosition,
                };
                p2 = {
                    x: right,
                    y: startPosition,
                };
                p3 = {
                    x: right,
                    y: endPosition,
                };
                p4 = {
                    x: right - endWidth,
                    y: endPosition,
                };
            }
        }
        return [p1, p2, p3, p4];
    };
    FunnelSeriesModel.prototype.renderPoints = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('funnel-points'),
        });
        this.state.points.forEach(function (point, dataIndex) {
            var pointList = _this.getFunnelItemPoints(dataIndex);
            group.add(new Polygon(__assign(__assign({ ref: point.shapeRef, pointList: pointList }, _this.getShapeAttr(point)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: _this.option.name,
                        point: _this.getWrappedPoint(dataIndex),
                    });
                } })));
        });
        return group;
    };
    FunnelSeriesModel.prototype.getGapBBox = function (point, nextPoint) {
        var isHorizontally = this._isHorizontally();
        return {
            top: isHorizontally
                ? (point.bbox.top + nextPoint.bbox.top) / 2
                : Math.min(point.bbox.top, nextPoint.bbox.top),
            bottom: isHorizontally
                ? (point.bbox.bottom + nextPoint.bbox.bottom) / 2
                : Math.max(point.bbox.bottom, nextPoint.bbox.bottom),
            left: isHorizontally
                ? Math.min(point.bbox.left, nextPoint.bbox.left)
                : (point.bbox.left + nextPoint.bbox.left) / 2,
            right: isHorizontally
                ? Math.max(point.bbox.right, nextPoint.bbox.right)
                : (point.bbox.right + nextPoint.bbox.right) / 2,
        };
    };
    FunnelSeriesModel.prototype.renderLabelAndConnectLine = function (labelOption, position, isBothPosition) {
        var _this = this;
        if (position === void 0) { position = labelOption.position; }
        if (isBothPosition === void 0) { isBothPosition = false; }
        var distance = labelOption.distance, placement = labelOption.placement, offsetX = labelOption.offsetX, offsetY = labelOption.offsetY, alignTo = labelOption.alignTo, margin = labelOption.margin;
        var group = new Group({
            key: this.getKey("funnel-label" + this.option.label.indexOf(labelOption) + position),
        });
        var inside = position.indexOf('inside') !== -1;
        var points = this.state.points.filter(function (point) { return (placement === 'on' ? true : point.visible); });
        points.forEach(function (point, dataIndex) {
            if (dataIndex === points.length - 1 && placement === 'between') {
                return;
            }
            var nextPoint = points[dataIndex + 1];
            var color = point.color, bbox = point.bbox;
            var textBbox = placement === 'on' ? bbox : _this.getGapBBox(point, nextPoint);
            var textPosition = getRectLabelPosition(textBbox, position, distance, _this._isHorizontally() ? 'top' : 'right');
            var x = textPosition.x, y = textPosition.y;
            var wrappedPoint = _this.getWrappedPoint(_this.state.points.indexOf(point));
            if (placement === 'between') {
                wrappedPoint.dataType = 'edge';
                wrappedPoint.data = {
                    source: _this._dataset.getRecord(dataIndex),
                    target: _this._dataset.getRecord(point.nextVisibleDataIndex),
                };
            }
            var text = isBothPosition && inside
                ? _this.formatItem(labelOption.formatter2, wrappedPoint)
                : _this.formatItem(labelOption.formatter, wrappedPoint);
            if (alignTo === 'edge' && !inside) {
                var labelSize = measureText(text, labelOption);
                if (!_this._isHorizontally()) {
                    var textSide = textPosition.textAlign === 'left' ? 'right' : 'left';
                    x = _this.getEdgePosition(textSide, margin, labelSize);
                }
                else {
                    var textSide = textPosition.textBaseline === 'top' ? 'bottom' : 'top';
                    y = _this.getEdgePosition(textSide, margin, labelSize);
                }
            }
            x += offsetX;
            y += offsetY;
            group.add(createRichText(__assign(__assign({ key: "label-" + dataIndex, display: point.visible, text: text,
                x: x,
                y: y, textAlign: textPosition.textAlign, textBaseline: textPosition.textBaseline, pointerEvents: inside ? 'none' : 'auto', fill: inside && placement === 'on' ? '#ffffff' : '#6E748E', stroke: color, lineWidth: inside && placement === 'on' ? 1 : 0 }, _this.getTextStyle(labelOption)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, eventGraph.LABEL, {
                        point: wrappedPoint,
                    });
                } })));
            if (!inside && _this.option.connectLine.show) {
                var lineStart = getRectLabelPosition(textBbox, position, 0, _this._isHorizontally() ? 'top' : 'right');
                var lineEnd = getRectLabelPosition(textBbox, position, distance - 2, _this._isHorizontally() ? 'top' : 'right');
                if (alignTo === 'edge') {
                    var labelSize = measureText(text, labelOption);
                    if (!_this._isHorizontally()) {
                        var textSide = textPosition.textAlign === 'left' ? 'right' : 'left';
                        lineEnd.x = _this.getEdgePosition(textSide, margin, labelSize);
                    }
                    else {
                        var textSide = textPosition.textBaseline === 'top' ? 'bottom' : 'top';
                        lineEnd.y = _this.getEdgePosition(textSide, margin, labelSize);
                    }
                }
                group.add(new Line(__assign({ key: "line-" + dataIndex, display: point.visible, x1: lineStart.x, y1: lineStart.y, x2: lineEnd.x, y2: lineEnd.y, stroke: color }, _this.getLineStyle(_this.option.connectLine))));
            }
        });
        return group;
    };
    FunnelSeriesModel.prototype.renderGapArea = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('gaparea'),
        });
        var points = this.state.points;
        points.slice(0, points.length - 1).forEach(function (current, dataIndex) {
            var point = _this.getWrappedPoint(dataIndex);
            var nextVisiblePoint = points[point.nextVisibleDataIndex];
            var currentPoints = _this.getFunnelItemPoints(dataIndex);
            var nextPoints = _this.getFunnelItemPoints(point.visible && nextVisiblePoint ? point.nextVisibleDataIndex : dataIndex);
            var p1 = currentPoints[3];
            var p2 = currentPoints[2];
            var p3 = nextPoints[1];
            var p4 = nextPoints[0];
            point.dataType = 'edge';
            point.data = {
                source: _this._dataset.getRecord(dataIndex),
                target: _this._dataset.getRecord(point.nextVisibleDataIndex),
            };
            if (nextVisiblePoint) {
                point.name = point.name + " - " + nextVisiblePoint.name;
                point.percent = (point.conversionRate * 100).toFixed(2) + "%";
                point.value = nextVisiblePoint.value;
            }
            var selected = _this._gapAreaSelectController.isSelected(point.name);
            group.add(new Polygon(__assign(__assign(__assign({ display: !!nextVisiblePoint, pointList: [p1, p2, p3, p4], fill: generateColors(_this.getSeriesColor())[0] }, _this.getShapeStyle(_this.option.gapAreaStyle)), (selected ? _this.getShapeStyle(_this.option.selected.gapAreaStyle) : null)), { data: point, onEvent: function (event) { return _this.handleUIEvent(event, 'gapArea', { point: point }); } })));
        });
        return group;
    };
    FunnelSeriesModel.prototype.renderBackground = function () {
        var _this = this;
        var _a = this.option, itemAlign = _a.itemAlign, backgroundStyle = _a.backgroundStyle;
        var _b = this.getFacetBBox(), top = _b.top, left = _b.left, bottom = _b.bottom, right = _b.right;
        var group = new Group(__assign({ key: this.getKey('funnel-background') }, backgroundStyle));
        this.state.points.forEach(function (point, dataIndex) {
            var p1;
            var p2;
            var p3;
            var p4;
            var startPosition = point.startPosition, endPosition = point.endPosition;
            var itemPoints = _this.getFunnelItemPoints(dataIndex);
            if (_this._isHorizontally()) {
                if (itemAlign === 'start') {
                    p1 = itemPoints[1];
                    p2 = itemPoints[2];
                    p3 = {
                        x: endPosition,
                        y: bottom,
                    };
                    p4 = {
                        x: startPosition,
                        y: bottom,
                    };
                }
                else if (itemAlign === 'center') {
                    p1 = {
                        x: startPosition,
                        y: top,
                    };
                    p2 = {
                        x: endPosition,
                        y: top,
                    };
                    p3 = {
                        x: endPosition,
                        y: bottom,
                    };
                    p4 = {
                        x: startPosition,
                        y: bottom,
                    };
                }
                else if (itemAlign === 'end') {
                    p1 = {
                        x: startPosition,
                        y: top,
                    };
                    p2 = {
                        x: endPosition,
                        y: top,
                    };
                    p3 = itemPoints[3];
                    p4 = itemPoints[0];
                }
            }
            else {
                // @ts-ignore compatible itemAlign old left
                // eslint-disable-next-line no-lonely-if
                if (itemAlign === 'left' || itemAlign === 'start') {
                    p1 = itemPoints[0];
                    p2 = {
                        x: right,
                        y: startPosition,
                    };
                    p3 = {
                        x: right,
                        y: endPosition,
                    };
                    p4 = itemPoints[3];
                }
                else if (itemAlign === 'center') {
                    p1 = {
                        x: left,
                        y: startPosition,
                    };
                    p2 = {
                        x: right,
                        y: startPosition,
                    };
                    p3 = {
                        x: right,
                        y: endPosition,
                    };
                    p4 = {
                        x: left,
                        y: endPosition,
                    };
                    // @ts-ignore compatible itemAlign old right
                }
                else if (itemAlign === 'right' || itemAlign === 'end') {
                    p1 = {
                        x: left,
                        y: startPosition,
                    };
                    p2 = itemPoints[1];
                    p3 = itemPoints[2];
                    p4 = {
                        x: left,
                        y: endPosition,
                    };
                }
            }
            group.add(new Polygon({
                pointList: [p1, p2, p3, p4],
                borderRadius: backgroundStyle.borderRadius,
            }));
        });
        return group;
    };
    FunnelSeriesModel.prototype.render = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('funnel'),
        });
        if (!this.visible()) {
            return group;
        }
        if (this.option.showBackground) {
            group.add(this.renderBackground());
        }
        group.add(this.renderPoints());
        if (this.option.showGapArea) {
            group.add(this.renderGapArea());
        }
        this.option.label.forEach(function (label) {
            if (!label.show) {
                return;
            }
            if (label.position === 'both') {
                group.add(_this.renderLabelAndConnectLine(label, 'outside', true));
                group.add(_this.renderLabelAndConnectLine(label, 'inside', true));
            }
            else {
                group.add(_this.renderLabelAndConnectLine(label));
            }
        });
        return group;
    };
    FunnelSeriesModel.prototype._isHorizontally = function () {
        var direction = this.option.direction;
        return ['LR', 'RL'].indexOf(direction) > -1;
    };
    FunnelSeriesModel.prototype._sortData = function () {
        var valueField = this.option.encode.value;
        var data = this._dataset.getData();
        if (this.option.sort === 'desc') {
            data = this._dataset.getData().sort(function (a, b) { return b[valueField] - a[valueField]; });
        }
        else if (this.option.sort === 'asc') {
            data = this._dataset.getData().sort(function (a, b) { return a[valueField] - b[valueField]; });
        }
        this._dataset.setData(data);
    };
    FunnelSeriesModel.prototype._hasInteraction = function (action) {
        return lodash.includes(this.option.gapAreaInteractions, action);
    };
    FunnelSeriesModel.dependencies = ['legend'];
    return FunnelSeriesModel;
}(SeriesModel));
export default FunnelSeriesModel;
registerChart('funnel', FunnelSeriesModel);
